import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { LEGEND, STATUS_TEXT, STATUS_THEME, levelStatus, sensorDisplayName, sensorPlace, spokenWater } from './levelStatus';
import { subscribeLatestLevels } from './sensorData';
import { formatWater } from './units';

const containerStyle = { width: '100%', height: '100%' };

const DEBUG = process.env.REACT_APP_DEBUG_MODE === 'true';
const debugLog = (...args) => {
  if (DEBUG) console.log('[SensorMap]', ...args);
};

const defaultCenter = {
  lat: parseFloat(process.env.REACT_APP_DEFAULT_MAP_LAT || '37.715323'),
  lng: parseFloat(process.env.REACT_APP_DEFAULT_MAP_LNG || '-122.493728'),
};

const libraries = ['marker', 'maps'];

// Google calls window.gm_authFailure when it rejects the API key (for example a
// site address missing from the key's website list). The map and its markers
// stop working after that, and marker setters throw.
let mapsAuthFailed = false;
const authFailureListeners = new Set();
if (typeof window !== 'undefined') {
  const previousAuthFailure = window.gm_authFailure;
  window.gm_authFailure = () => {
    mapsAuthFailed = true;
    authFailureListeners.forEach((listener) => listener());
    if (typeof previousAuthFailure === 'function') previousAuthFailure();
  };
}

const updateMarker = (update) => {
  try {
    update();
  } catch (err) {
    console.warn('[SensorMap] marker update skipped:', err.message);
  }
};

const mapOptions = {
  mapId: (process.env.REACT_APP_GOOGLE_MAPS_ID || 'DEMO_MAP_ID').replace(/^\uFEFF/, '').trim(),
  mapTypeId: 'hybrid',
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: true,
  scaleControl: true,
  mapTypeControl: true,
  fullscreenControl: true,
};

const describeMarker = (sensor, latest, unitSystem, nowMs) => {
  const place = sensorPlace(sensor, sensor.id);
  const name = place.deviceId && place.deviceId !== place.title
    ? `${place.title}, ${place.deviceId}`
    : place.title;
  const status = levelStatus(latest?.levelMm, sensor.yellowMm, sensor.redMm, latest?.atMs, nowMs);
  const theme = STATUS_THEME[status];
  const spoken = spokenWater(latest?.levelMm, unitSystem);
  const label = latest ? formatWater(latest.levelMm, unitSystem) : '—';
  return {
    status,
    theme,
    label,
    aria: `${name}, ${STATUS_TEXT[status].name}, ${spoken}`,
  };
};

const createMarkerElement = () => {
  const el = document.createElement('div');
  el.className = 'sensor-pin';
  el.tabIndex = 0;
  el.setAttribute('role', 'button');
  Object.assign(el.style, {
    minWidth: '44px',
    minHeight: '44px',
    padding: '0 12px',
    borderRadius: '999px',
    border: '3px solid #ffffff',
    boxSizing: 'border-box',
    fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transformOrigin: 'center center',
  });
  return el;
};

const SensorMap = ({
  sensors,
  unitSystem,
  selectedId,
  onSensorSelect,
  onMapReady,
  onLevels,
  levels,
  layoutToken,
}) => {
  const [map, setMap] = useState(null);
  const [latestByDevice, setLatestByDevice] = useState({});
  const [dataError, setDataError] = useState(null);
  const [authFailed, setAuthFailed] = useState(mapsAuthFailed);
  const markersRef = useRef(new Map());
  const paintRef = useRef(() => {});
  const onSelectRef = useRef(onSensorSelect);
  const onLevelsRef = useRef(onLevels);
  const onMapReadyRef = useRef(onMapReady);

  const mapsApiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').replace(/^\uFEFF/, '').trim();
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: mapsApiKey,
    libraries,
  });

  useEffect(() => {
    onSelectRef.current = onSensorSelect;
  }, [onSensorSelect]);
  useEffect(() => {
    onLevelsRef.current = onLevels;
  }, [onLevels]);
  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  useEffect(() => {
    const onAuthFailure = () => {
      console.error('[SensorMap] Google Maps rejected the API key for this site address.');
      setAuthFailed(true);
    };
    authFailureListeners.add(onAuthFailure);
    if (mapsAuthFailed) setAuthFailed(true);
    return () => {
      authFailureListeners.delete(onAuthFailure);
    };
  }, []);

  useEffect(() => {
    if (levels) {
      setLatestByDevice(levels);
      return undefined;
    }
    return subscribeLatestLevels(
      (byDevice) => {
        debugLog('waterLevel_latest update', byDevice);
        setDataError(null);
        setLatestByDevice(byDevice);
        onLevelsRef.current?.(byDevice);
      },
      (err) => {
        console.error('[SensorMap] waterLevel_latest:', err);
        setDataError('Live water levels are unavailable. Marker colors may be out of date.');
      }
    );
  }, [levels]);

  useEffect(() => {
    paintRef.current = () => {
      const nowMs = Date.now();
      markersRef.current.forEach(({ element, marker, sensor }) => {
        const { theme, label, aria, status } = describeMarker(
          sensor,
          latestByDevice[sensor.id],
          unitSystem,
          nowMs
        );
        const selected = sensor.id === selectedId;
        element.style.backgroundColor = theme.marker;
        element.style.color = theme.text;
        element.style.transform = selected ? 'scale(1.12)' : 'scale(1)';
        element.style.boxShadow = selected ? '0 0 0 3px #ffffff, 0 0 0 5px #1B1D1C' : 'none';
        element.style.zIndex = selected ? '2' : '1';
        element.textContent = label;
        element.setAttribute('aria-label', aria);
        element.removeAttribute('aria-current');
        if (selected) element.setAttribute('aria-current', 'true');
        updateMarker(() => {
          marker.title = aria;
          marker.zIndex = selected ? 2 : 1;
        });
        if (status) element.dataset.status = status;
      });
    };
    paintRef.current();
  }, [latestByDevice, selectedId, unitSystem]);

  useEffect(() => {
    if (!map || !isLoaded || authFailed) return undefined;
    let cancelled = false;
    const markers = markersRef.current;

    const createMarkers = async () => {
      const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker');
      if (cancelled) return;
      sensors
        .filter((sensor) => sensor.latitude != null && sensor.longitude != null)
        .forEach((sensor) => {
          const element = createMarkerElement();
          const select = (event) => {
            event.stopPropagation();
            onSelectRef.current?.(sensor.id);
          };
          element.addEventListener('click', select);
          element.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            select(event);
          });
          const marker = new AdvancedMarkerElement({
            map,
            position: { lat: sensor.latitude, lng: sensor.longitude },
            content: element,
            title: sensorDisplayName(sensor, sensor.id),
          });
          markers.set(sensor.id, { marker, element, sensor });
        });
      paintRef.current();
      debugLog(`Created ${markers.size} markers`);
    };

    createMarkers().catch((err) => {
      console.error('[SensorMap] markers:', err);
      setDataError('The sensor markers could not be drawn. Refresh the page to try again.');
    });

    return () => {
      cancelled = true;
      markers.forEach(({ marker }) => {
        updateMarker(() => {
          marker.map = null;
        });
      });
      markers.clear();
    };
  }, [map, isLoaded, sensors, authFailed]);

  useEffect(() => {
    if (!map || !window.google?.maps?.event) return undefined;
    const id = window.requestAnimationFrame(() => {
      window.google.maps.event.trigger(map, 'resize');
    });
    return () => window.cancelAnimationFrame(id);
  }, [map, layoutToken]);

  const onLoad = useCallback((loadedMap) => {
    loadedMap.setMapTypeId('hybrid');
    setMap(loadedMap);
    onMapReadyRef.current?.(loadedMap);
  }, []);
  const onUnmount = useCallback(() => {
    setMap(null);
    onMapReadyRef.current?.(null);
  }, []);

  if (loadError || authFailed) {
    return (
      <div className="map-status" role="alert">
        The map could not be loaded. Use “Choose from list” to pick a location.
      </div>
    );
  }
  if (!isLoaded) return <div className="map-status">Loading map…</div>;

  return (
    <div className="map-frame">
      {dataError && <div className="map-error" role="alert">{dataError}</div>}
      <GoogleMap
        mapContainerStyle={containerStyle}
        mapContainerClassName="google-map"
        center={defaultCenter}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
      />
      <ul className="map-legend">
        {LEGEND.map((item) => (
          <li key={item.id}>
            <span className="legend-swatch" style={{ background: STATUS_THEME[item.id].marker }} />
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SensorMap;
